import React, { useCallback, useEffect } from "react";
import css from "./EnquiryFormUserDetails.module.scss";
import {
    EmailIcon,
    PeopleIcon,
    PhoneIcon,
    UserIcon,
    BookingIcon
} from "@assets/icons";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useFormContext } from "react-hook-form";
import { EnquiryFormUserDetailsType } from "./types";
import Input from "@ui/Input";
import InputGroup from "@ui/InputGroup";
import BookedVenue from "@components/BookedVenue";
import ClientOnly from "../../ClientOnly";
import useUser from "@hooks/useUser";
import { IBookedResult } from "@components/BookedVenue/BookedVenue";
import useOpenClose from "@hooks/useOpenClose";
import cl from "classnames";
import { USER_LOCATION_COOKIE, USER_VENUE_ID_COOKIE, USER_VENUE_NAME_COOKIE } from "@helper/constants";
import { getCookieByName } from "@helper/cookies";

const UserSection = React.lazy(() => import('./UserSection'));

function EnquiryFormUserDetails({
    showMasterVenue,
    setMasterVenueUpdated,
    isEngagementVenue,
    hideGuest,
}: {
    showMasterVenue: boolean,
    setMasterVenueUpdated: (value: boolean) => void,
    isEngagementVenue: boolean,
    hideGuest?: boolean,
}) {
    const {
        register,
        formState: { isValid, errors },
        getValues,
        watch,
    } = useFormContext<EnquiryFormUserDetailsType>();
    const { isOpen, toggle } = useOpenClose(true);
    const ICON_SIZE = 17;
    const engagementDate = watch("engagementDate");
    const weddingDate = watch("date");
    return (
        <div className={cl(css.container, { [css.collapsed]: !isOpen })}>
            <div className={css.header} onClick={toggle}>
                <span>Your details{!isValid && <span className={css.hint}>(incomplete)</span>}</span>
                {isOpen ? <Icon icon={faMinus} /> : <Icon icon={faPlus} />}
            </div>
            <ClientOnly Component={UserSection} />
            <InputGroup postfix={errors.name
                ? <InputGroup.HintText>{errors.name.message}</InputGroup.HintText>
                : null
            }>
                <UserIcon />
                <Input
                    id="enquiry-form-name"
                    placeholder="Name"
                    isInvalid={!!errors.name}
                    data-testid="enquiry-form-user-name"
                    {...register("name", { required: "Enter your full name" })}
                />
            </InputGroup>
            <InputGroup postfix={errors.email
                ? <InputGroup.HintText>{errors.email.message}</InputGroup.HintText>
                : null
            }>
                <EmailIcon />
                <Input
                    id="enquiry-form-email"
                    placeholder="Email"
                    isInvalid={!!errors.email}
                    data-testid="enquiry-form-user-email"
                    {...register("email", {
                        required: "Email is required",
                        pattern: {
                            value: /^\S+@\S+$/i,
                            message: "Invalid email address",
                        },
                    })}
                />
            </InputGroup>
            <InputGroup postfix={errors.phone
                ? <InputGroup.HintText>{errors.phone.message}</InputGroup.HintText>
                : null
            }>
                <PhoneIcon />
                <Input
                    id="enquiry-form-phone"
                    placeholder="Phone"
                    isInvalid={!!errors.phone}
                    data-testid="enquiry-form-user-phone"
                    {...register("phone", {
                        required: "Phone is required",
                    })}
                />
            </InputGroup>

            {isEngagementVenue && <InputGroup postfix={errors.engagementDate
                    ? <InputGroup.HintText>{errors.engagementDate.message}</InputGroup.HintText>
                    : null
                }>
                    <BookingIcon width={ICON_SIZE} height={ICON_SIZE} />
                    <Input
                        id="enquiry-form-date"
                        className={`text-start ${!engagementDate ? 'ew-input-empty' : ''}`}
                        placeholder="Engagement date"
                        isInvalid={!!errors.engagementDate}
                        data-testid="enquiry-form-user-engagement-date"
                        type="date"
                        {...register("engagementDate", {
                            onChange: (e) => {
                                if (e.target.value) {
                                    e.target.classList.remove('ew-input-empty');
                                } else {
                                    e.target.classList.add('ew-input-empty');
                                }
                            },
                            validate: (value) => {
                                if(!isEngagementVenue) {
                                    return true;
                                }
                                if (!value) return "Engagement date is required";
                                const selectedDate = new Date(value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                const weddingDate = getValues("date");
                                if (weddingDate) {
                                    const weddingDateObj = new Date(weddingDate);
                                    if (selectedDate >= weddingDateObj) {
                                        return "Engagement date must be before wedding date";
                                    }
                                }

                                return selectedDate >= today || "Please enter a date in the future";
                            }
                        })}
                />
            </InputGroup>}
            <div className={hideGuest ? undefined : css.twoFields}>
                {!hideGuest && <InputGroup postfix={errors.guests
                    ? <InputGroup.HintText>{errors.guests.message}</InputGroup.HintText>
                    : null
                }>
                    <PeopleIcon />
                    <Input
                        id="enquiry-form-guests"
                        placeholder="Guests"
                        type="number"
                        data-testid="enquiry-form-user-guest-count"
                        isInvalid={!!errors.guests}
                        {...register("guests", {
                            required: "Guests is required",
                            max: {
                                value: 10000,
                                message: "Max 10000 guests",
                            },
                        })}
                    />
                </InputGroup>}
                <InputGroup postfix={errors.date
                    ? <InputGroup.HintText>{errors.date.message}</InputGroup.HintText>
                    : null
                }>
                    <BookingIcon width={ICON_SIZE} height={ICON_SIZE} />
                    <Input
                        id="enquiry-form-date"
                        className={`text-start ${!weddingDate ? 'ew-input-empty' : ''}`}
                        placeholder="Wedding date"
                        isInvalid={!!errors.date}
                        data-testid="enquiry-form-user-wedding-date"
                        type="date"
                        {...register("date", {
                            onChange: (e) => {
                                if(e.target.value) {
                                    e.target.classList.remove('ew-input-empty');
                                } else {
                                    e.target.classList.add('ew-input-empty');
                                }
                            },
                            validate: (value) => {
                                if (!value) return true;
                                const selectedDate = new Date(value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                const engagementDate = getValues("engagementDate");
                                if (engagementDate && isEngagementVenue) {
                                    const engagementDateObj = new Date(engagementDate);
                                    if (selectedDate <= engagementDateObj) {
                                        return "Wedding date must be after engagement date";
                                    }
                                }

                                return selectedDate >= today || "Please enter a date in the future";
                            }
                        })}
                    />
                </InputGroup>
            </div>

            {showMasterVenue && <div>
                <MasterVenueSection setMasterVenueUpdated={setMasterVenueUpdated} />
            </div>}
        </div>
    );
}

function MasterVenueSection({ setMasterVenueUpdated }: { setMasterVenueUpdated: (value: boolean) => void }) {
    const {
        register,
        setValue,
        getValues,
    } = useFormContext<EnquiryFormUserDetailsType>();
    const { user, isLoading } = useUser();
    useEffect(() => {
        if (!!user?.id) {
            !!user.regionId && setValue("regionId", user.regionId);
            !!user.bookedVenueSupplierName && setValue("venueName", user.bookedVenueSupplierName);
            !!user.bookedVenueSupplierId && setValue("venueId", user.bookedVenueSupplierId);
        }
        else {
            const cookieFields: Record<string, string> = {
                regionId: USER_LOCATION_COOKIE,
                venueName: USER_VENUE_NAME_COOKIE,
                venueId: USER_VENUE_ID_COOKIE,
            };

            Object.entries(cookieFields).forEach(([field, cookieName]) => {
                const cookieValue = getCookieByName(cookieName);

                // Parse regionId and venueId as numbers, default to 0
                const parsedValue =
                    field === "regionId" || field === "venueId"
                        ? parseInt(cookieValue || "0", 10)
                        : cookieValue || "";

                setValue(field as keyof EnquiryFormUserDetailsType, parsedValue);
            });
        }
    }, [user]);

    const { venueName, venueId, regionId, date } = getValues() || {};
    const onChangeBookedBenue = useCallback((result: IBookedResult) => {
        if (!!result && !!result.venueSupplierId && user.bookedVenueSupplierId !== result.venueSupplierId) {
            setMasterVenueUpdated(true);
        }
        setValue("venueName", !!result.venueSupplierName ? result.venueSupplierName : '');
        setValue("venueId", !!result.venueSupplierId ? result.venueSupplierId : 0);
        setValue("regionId", !!result.regionId ? result.regionId : 0);
    }, [setValue, user.bookedVenueSupplierId, setMasterVenueUpdated]);

    if (isLoading) {
        return null;
    }
    return (<>
        <BookedVenue
            venueSupplierName={venueName}
            venueSupplierId={venueId}
            regionId={regionId}
            onChange={onChangeBookedBenue}
        />
        <div style={{ display: 'none' }}>
            <Input
                id="enquiry-form-region"
                type="hidden"
                {...register("regionId")}
            />
            <Input
                id="enquiry-form-region-name"
                type="hidden"
                {...register("regionName")}
            />
            <Input
                id="enquiry-form-venue-name"
                type="hidden"
                {...register("venueName")}
            />
            <Input
                id="enquiry-form-venue-id"
                type="hidden"
                {...register("venueId")}
            />
        </div>
    </>)
}

export default EnquiryFormUserDetails;
